import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  Plane,
  Search,
  Plus,
  CreditCard,
  Palette,
  MoreVertical,
  Eye,
  Trash2,
  FileText,
  Hotel as HotelIcon,
  Pencil,
  Bell,
  Download,
  FileSpreadsheet,
  CalendarClock,
  LogOut,
  Calendar as CalendarIcon,
  LayoutList,
  LayoutGrid,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Users,
  History,
  DollarSign,
  Filter,
  CalendarDays,
  Settings,
  ChevronDown,
  SlidersHorizontal,
  TrendingUp,
  Wallet,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  RotateCcw,
  Sparkles,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Semaphore } from "@/components/Semaphore";
import { PaymentBadges } from "@/components/PaymentBadge";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { ClientDetailSheet } from "@/components/ClientDetailSheet";
import { ReservationDialog } from "@/components/ReservationDialog";
import {
  PaymentMethodsDialog,
  StatusLabelsDialog,
} from "@/components/SettingsDialogs";
import { CalendarView } from "@/components/CalendarView";
import { ClientCardsView } from "@/components/ClientCardsView";
import { UsersDialog } from "@/components/UsersDialog";
import { AuditDialog } from "@/components/AuditDialog";
import { TerminalsDialog } from "@/components/TerminalsDialog";
import {
  listClients,
  getSettings,
  updateStatus,
  deleteClient,
  getReminders,
  exportUrl,
  logout,
  getTerminals,
} from "@/lib/api";
import { STATUS_META, STATUS_ORDER, formatMoney, formatDate } from "@/lib/constants";

export default function Dashboard({ user, onLogout }) {
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({
    payment_methods: [],
    status_labels: {},
    currency: "USD",
    exchange_rates: { USD: 1.0, MXN: 18.5, EUR: 0.92 },
  });
  const [reminders, setReminders] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [terminalsOpen, setTerminalsOpen] = useState(false);
  const [terminalFilter, setTerminalFilter] = useState("todos");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [payFilter, setPayFilter] = useState("todos");
  const [quickFilter, setQuickFilter] = useState("todos");
  const [loading, setLoading] = useState(true);

  // View mode: 'cards', 'table', or 'calendar'
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("viajacrm_view_mode");
    if (saved === "table" || saved === "cards" || saved === "calendar") {
      return saved;
    }
    return "cards";
  });
  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("viajacrm_view_mode", mode);
  };

  // Currency switcher
  const [currency, setCurrency] = useState("USD");

  // Sorting
  const [sortField, setSortField] = useState("travel_date");
  const [sortAsc, setSortAsc] = useState(true);

  // Modals state
  const [newOpen, setNewOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [detailClient, setDetailClient] = useState(null);
  const [resClient, setResClient] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [usersOpen, setUsersOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const userInitials = useMemo(() => {
    if (!user) return "AG";
    if (user.full_name) {
      const parts = user.full_name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return user.full_name.slice(0, 2).toUpperCase();
    }
    if (user.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return "AG";
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, rem, terms] = await Promise.all([
        listClients(),
        getSettings(),
        getReminders(15),
        getTerminals(),
      ]);
      setClients(c);
      setSettings(s);
      setReminders(rem);
      setTerminals(terms || []);
    } catch {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const loadTerminals = async () => {
    try {
      const terms = await getTerminals();
      setTerminals(terms || []);
    } catch {
      /* noop */
    }
  };

  const refreshReminders = async () => {
    try {
      setReminders(await getReminders(15));
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upsert = (updated) =>
    setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  // Currency converter
  const convertAmount = (valUsd) => {
    const rate = settings.exchange_rates?.[currency] || 1.0;
    return (Number(valUsd) || 0) * rate;
  };

  // Financial metrics in active currency
  const financials = useMemo(() => {
    let salesUsd = 0;
    let paidUsd = 0;
    let pendingClientsCount = 0;

    clients.forEach((c) => {
      const tot = Number(c.total_amount) || 0;
      const pd = (c.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      salesUsd += tot;
      paidUsd += pd;
      if (tot - pd > 0.01) {
        pendingClientsCount += 1;
      }
    });

    const balanceUsd = Math.max(0, salesUsd - paidUsd);
    const percentPaid = salesUsd > 0 ? Math.min(100, Math.round((paidUsd / salesUsd) * 100)) : 0;
    const r = settings.exchange_rates?.[currency] || 1.0;

    return {
      salesUsd,
      paidUsd,
      balanceUsd,
      sales: salesUsd * r,
      paid: paidUsd * r,
      balance: balanceUsd * r,
      percentPaid,
      pendingClientsCount,
    };
  }, [clients, currency, settings.exchange_rates]);

  // Contextual greeting
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Buenos días";
    if (hour < 19) return "Buenas tardes";
    return "Buenas noches";
  }, []);

  // Formatted current date in Spanish
  const formattedToday = useMemo(() => {
    const d = new Date();
    const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
    const str = d.toLocaleDateString("es-ES", options);
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, []);

  const hasActiveFilters = Boolean(
    search ||
    statusFilter !== "todos" ||
    payFilter !== "todos" ||
    terminalFilter !== "todos" ||
    quickFilter !== "todos"
  );

  const resetAllFilters = () => {
    setSearch("");
    setStatusFilter("todos");
    setPayFilter("todos");
    setTerminalFilter("todos");
    setQuickFilter("todos");
  };

  // Sorting and filtering
  const filtered = useMemo(() => {
    const today = new Date();
    const curMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

    const list = clients.filter((c) => {
      const q = search.toLowerCase();
      const matchQ =
        !q ||
        c.full_name.toLowerCase().includes(q) ||
        c.client_code.toLowerCase().includes(q) ||
        (c.hotel || "").toLowerCase().includes(q) ||
        (c.terminal || "").toLowerCase().includes(q) ||
        (c.assigned_to || "").toLowerCase().includes(q);

      const matchS = statusFilter === "todos" || c.status === statusFilter;
      const pm = c.payment_methods?.length ? c.payment_methods : c.payment_method ? [c.payment_method] : [];
      const matchP = payFilter === "todos" || pm.includes(payFilter);
      const matchTerminal =
        terminalFilter === "todos" ||
        c.terminal === terminalFilter ||
        (c.payments || []).some((p) => p.terminal === terminalFilter);

      const paid = (c.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
      const balance = Math.max(0, (Number(c.total_amount) || 0) - paid);
      const tdate = c.reservation?.travel_date || "";

      let matchQuick = true;
      if (quickFilter === "con_saldo") matchQuick = balance > 0;
      else if (quickFilter === "sin_vuelos") matchQuick = !c.reservation?.has_flights;
      else if (quickFilter === "este_mes") matchQuick = tdate.startsWith(curMonth);

      return matchQ && matchS && matchP && matchQuick && matchTerminal;
    });

    if (sortField) {
      list.sort((a, b) => {
        let valA, valB;
        if (sortField === "name") {
          valA = a.full_name.toLowerCase();
          valB = b.full_name.toLowerCase();
        } else if (sortField === "code") {
          valA = a.client_code;
          valB = b.client_code;
        } else if (sortField === "travel_date") {
          valA = a.reservation?.travel_date || "9999-99-99";
          valB = b.reservation?.travel_date || "9999-99-99";
        } else if (sortField === "hotel") {
          valA = (a.hotel || "").toLowerCase();
          valB = (b.hotel || "").toLowerCase();
        } else if (sortField === "terminal") {
          valA = (a.terminal || "").toLowerCase();
          valB = (b.terminal || "").toLowerCase();
        } else if (sortField === "total") {
          valA = Number(a.total_amount) || 0;
          valB = Number(b.total_amount) || 0;
        } else if (sortField === "paid") {
          valA = (a.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
          valB = (b.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
        } else if (sortField === "balance") {
          const pA = (a.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
          const pB = (b.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
          valA = Math.max(0, (Number(a.total_amount) || 0) - pA);
          valB = Math.max(0, (Number(b.total_amount) || 0) - pB);
        } else if (sortField === "status") {
          valA = a.status;
          valB = b.status;
        }

        if (valA < valB) return sortAsc ? -1 : 1;
        if (valA > valB) return sortAsc ? 1 : -1;
        return 0;
      });
    }

    return list;
  }, [clients, search, statusFilter, payFilter, terminalFilter, quickFilter, sortField, sortAsc]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const stats = useMemo(() => {
    const s = { verde: 0, amarillo: 0, rojo: 0, gris: 0 };
    clients.forEach((c) => (s[c.status] = (s[c.status] || 0) + 1));
    return s;
  }, [clients]);

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateStatus(id, status);
      upsert(updated);
    } catch {
      toast.error("No se pudo cambiar el estatus");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteClient(deleteTarget.id);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      toast.success("Cliente eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  const labels = settings.status_labels;
  const detail = detailClient ? clients.find((c) => c.id === detailClient.id) || detailClient : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-2xs">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          
          {/* Left: Brand & Currency Pill */}
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 flex items-center justify-center shadow-xs">
                <Plane className="h-4.5 w-4.5 text-white -rotate-12" />
              </div>
              <div className="leading-tight">
                <div className="flex items-center gap-1.5">
                  <span className="font-heading font-bold text-slate-900 text-base sm:text-lg tracking-tight">ViajaCRM</span>
                  <span className="hidden sm:inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200/60 tracking-wider">PRO</span>
                </div>
                <p className="text-[11px] text-slate-400 font-medium -mt-0.5 truncate hidden xs:block">Gestión de Reservas</p>
              </div>
            </div>

            <div className="h-5 w-px bg-slate-200 hidden sm:block" />

            {/* Currency selector (compact, sleek) */}
            <div className="hidden sm:flex items-center rounded-lg border border-slate-200/90 bg-slate-50/80 hover:bg-slate-100/70 px-2 py-1 transition-colors">
              <DollarSign className="h-3.5 w-3.5 text-slate-400 mr-1 shrink-0" />
              <span className="text-[11px] font-medium text-slate-500 mr-1 hidden lg:inline">Moneda:</span>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-5 w-[74px] border-0 bg-transparent p-0 text-xs font-bold text-slate-800 focus:ring-0 shadow-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="start">
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="MXN">MXN ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right: Structured Menus, Notifications, CTA, and Profile */}
          <div className="flex items-center gap-1.5 sm:gap-2">

            {/* 1. Menú: Reportes y Exportación */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-2.5 sm:px-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium text-xs sm:text-sm gap-1.5 hidden md:inline-flex"
                  data-testid="export-button"
                >
                  <FileSpreadsheet className="h-4 w-4 text-slate-500" />
                  <span>Reportes</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 p-1.5 shadow-lg border-slate-200">
                <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                  Exportación de Datos
                </DropdownMenuLabel>
                <DropdownMenuItem asChild data-testid="export-excel" className="cursor-pointer py-2 rounded-md">
                  <a href={exportUrl("xlsx")} className="flex items-center gap-2.5 w-full">
                    <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-100">
                      <FileSpreadsheet className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-xs font-semibold text-slate-800">Exportar a Excel</span>
                      <span className="text-[10px] text-slate-400">Descargar libro con formato .xlsx</span>
                    </div>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="export-pdf" className="cursor-pointer py-2 rounded-md">
                  <a href={exportUrl("pdf")} className="flex items-center gap-2.5 w-full">
                    <div className="h-8 w-8 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center shrink-0 border border-rose-100">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-xs font-semibold text-slate-800">Exportar a PDF</span>
                      <span className="text-[10px] text-slate-400">Documento imprimible consolidado</span>
                    </div>
                  </a>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="my-1 bg-slate-100" />

                <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                  Control y Seguridad
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setAuditOpen(true)}
                  className="cursor-pointer py-2 rounded-md flex items-center gap-2.5"
                >
                  <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0 border border-blue-100">
                    <History className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-semibold text-slate-800">Registro de Auditoría</span>
                    <span className="text-[10px] text-slate-400">Bitácora de cambios y acciones</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 2. Menú: Configuración */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-2.5 sm:px-3 text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium text-xs sm:text-sm gap-1.5 hidden md:inline-flex"
                  data-testid="settings-menu-button"
                >
                  <Settings className="h-4 w-4 text-slate-500" />
                  <span>Configuración</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 p-1.5 shadow-lg border-slate-200">
                <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                  Cobranza y Pasarelas
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setTerminalsOpen(true)}
                  data-testid="manage-terminals-button"
                  className="cursor-pointer py-2 rounded-md flex items-center gap-2.5"
                >
                  <div className="h-8 w-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0 border border-blue-100">
                    <CreditCard className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col text-left flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800">Terminales de Cobro</span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded-full bg-blue-50 text-blue-700 border border-blue-200/60">
                        {terminals.filter((t) => t.is_active).length} activas
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 truncate">POS bancarios y estado activo/bloqueado</span>
                  </div>
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setPayOpen(true)}
                  data-testid="manage-payments-button"
                  className="cursor-pointer py-2 rounded-md flex items-center gap-2.5"
                >
                  <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-100">
                    <DollarSign className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-semibold text-slate-800">Formas de Pago</span>
                    <span className="text-[10px] text-slate-400">Catálogo de métodos aceptados</span>
                  </div>
                </DropdownMenuItem>

                <DropdownMenuSeparator className="my-1 bg-slate-100" />

                <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                  Personalización
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setStatusOpen(true)}
                  data-testid="manage-status-button"
                  className="cursor-pointer py-2 rounded-md flex items-center gap-2.5"
                >
                  <div className="h-8 w-8 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center shrink-0 border border-purple-100">
                    <Palette className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-semibold text-slate-800">Semáforo de Estados</span>
                    <span className="text-[10px] text-slate-400">Etiquetas y colores de seguimiento</span>
                  </div>
                </DropdownMenuItem>

                {user?.role === "admin" && (
                  <>
                    <DropdownMenuSeparator className="my-1 bg-slate-100" />
                    <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">
                      Administración
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => setUsersOpen(true)}
                      data-testid="manage-users-button"
                      className="cursor-pointer py-2 rounded-md flex items-center gap-2.5"
                    >
                      <div className="h-8 w-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 border border-amber-100">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col text-left">
                        <span className="text-xs font-semibold text-slate-800">Gestión de Agentes</span>
                        <span className="text-[10px] text-slate-400">Usuarios, contraseñas y roles</span>
                      </div>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu (Quick overflow for screens < md) */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 text-slate-600 rounded-lg"
                    title="Menú de herramientas"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 p-1.5 shadow-lg border-slate-200">
                  <div className="px-2.5 py-2 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-600">Moneda</span>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger className="h-7 w-24 border border-slate-200 text-xs font-bold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent align="end">
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="MXN">MXN ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 pt-2 pb-1">
                    Configuración
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setTerminalsOpen(true)} className="py-2 text-xs">
                    <CreditCard className="h-4 w-4 mr-2 text-blue-600" /> Terminales de Cobro
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPayOpen(true)} className="py-2 text-xs">
                    <DollarSign className="h-4 w-4 mr-2 text-emerald-600" /> Formas de Pago
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusOpen(true)} className="py-2 text-xs">
                    <Palette className="h-4 w-4 mr-2 text-purple-600" /> Semáforo de Estados
                  </DropdownMenuItem>
                  {user?.role === "admin" && (
                    <DropdownMenuItem onClick={() => setUsersOpen(true)} className="py-2 text-xs">
                      <Users className="h-4 w-4 mr-2 text-amber-600" /> Gestión de Agentes
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator className="my-1 bg-slate-100" />
                  <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 pt-1 pb-1">
                    Reportes
                  </DropdownMenuLabel>
                  <DropdownMenuItem asChild className="py-2 text-xs">
                    <a href={exportUrl("xlsx")} className="flex items-center">
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Exportar a Excel
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="py-2 text-xs">
                    <a href={exportUrl("pdf")} className="flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-rose-600" /> Exportar a PDF
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAuditOpen(true)} className="py-2 text-xs">
                    <History className="h-4 w-4 mr-2 text-blue-600" /> Auditoría
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* 3. Recordatorios Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="relative h-9 w-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 transition-colors shadow-2xs"
                  data-testid="reminders-button"
                  title="Recordatorios de Viaje"
                >
                  <Bell className="h-4 w-4" />
                  {reminders.length > 0 && (
                    <span
                      className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white animate-pulse"
                      data-testid="reminders-count"
                    >
                      {reminders.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 shadow-lg border-slate-200" data-testid="reminders-panel">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 rounded-t-md">
                  <p className="font-heading font-semibold text-slate-800 flex items-center gap-2 text-sm">
                    <CalendarClock className="h-4 w-4 text-blue-600" /> Recordatorios de Viaje
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Clientes con viajes en los próximos 15 días</p>
                </div>
                <div className="max-h-80 overflow-auto p-2 divide-y divide-slate-100">
                  {reminders.length === 0 ? (
                    <div className="py-8 text-center">
                      <CalendarClock className="h-8 w-8 text-slate-300 mx-auto mb-2 stroke-1" />
                      <p className="text-sm font-medium text-slate-500">No hay viajes próximos</p>
                      <p className="text-xs text-slate-400 mt-0.5">Los viajes de los próximos 15 días aparecerán aquí.</p>
                    </div>
                  ) : (
                    reminders.map((r) => {
                      const m = STATUS_META[r.status] || STATUS_META.gris;
                      return (
                        <button
                          key={r.id}
                          data-testid="reminder-item"
                          onClick={() => {
                            const c = clients.find((x) => x.id === r.id);
                            if (c) setDetailClient(c);
                          }}
                          className="w-full text-left flex items-center gap-3 rounded-lg p-2.5 hover:bg-slate-50 transition-colors"
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-slate-100"
                            style={{ backgroundColor: m.hex }}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-700 truncate">
                              {r.full_name}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{r.hotel || "Sin hotel registrado"}</p>
                          </div>
                          <span
                            className={`text-[11px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${
                              r.days_left <= 3
                                ? "bg-red-50 text-red-600 border border-red-200/60"
                                : "bg-amber-50 text-amber-600 border border-amber-200/60"
                            }`}
                          >
                            {r.days_left === 0
                              ? "Hoy"
                              : r.days_left === 1
                              ? "Mañana"
                              : `En ${r.days_left} días`}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* 4. Primary CTA: + Nuevo Cliente */}
            <Button
              size="sm"
              data-testid="new-client-button"
              onClick={() => setNewOpen(true)}
              className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs sm:text-sm px-3 sm:px-4 rounded-lg shadow-xs hover:shadow transition-all"
            >
              <Plus className="h-4 w-4 mr-1 sm:mr-1.5 stroke-[2.5]" />
              <span className="hidden xs:inline">Nuevo Cliente</span>
              <span className="xs:hidden">Nuevo</span>
            </Button>

            {/* Subtle Divider */}
            <div className="h-5 w-px bg-slate-200 mx-0.5 hidden sm:block" />

            {/* 5. User Profile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 p-1 pl-1.5 pr-2 rounded-lg hover:bg-slate-100 transition-colors outline-hidden focus:ring-2 focus:ring-slate-300"
                  data-testid="user-profile-button"
                  title={`Usuario: ${user?.full_name || user?.username || "Usuario"}`}
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-700 text-white font-bold text-xs flex items-center justify-center ring-2 ring-white shadow-2xs">
                    {userInitials}
                  </div>
                  <div className="hidden lg:flex flex-col text-left leading-tight">
                    <span className="text-xs font-semibold text-slate-800 truncate max-w-[100px]">
                      {user?.full_name || user?.username || "Agente"}
                    </span>
                    <span className="text-[10px] text-slate-400 capitalize">
                      {user?.role === "admin" ? "Administrador" : "Agente"}
                    </span>
                  </div>
                  <ChevronDown className="h-3 w-3 text-slate-400 hidden lg:block opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-1.5 shadow-lg border-slate-200">
                <div className="px-3 py-2 border-b border-slate-100 mb-1">
                  <p className="text-xs font-bold text-slate-900 leading-tight truncate">
                    {user?.full_name || user?.username || "Usuario"}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[10px] font-mono text-slate-400">@{user?.username || "usuario"}</span>
                    <span
                      className={`text-[9px] font-semibold uppercase px-1.5 py-0.2 rounded-full ${
                        user?.role === "admin"
                          ? "bg-blue-50 text-blue-700 border border-blue-200/60"
                          : "bg-slate-100 text-slate-600 border border-slate-200/60"
                      }`}
                    >
                      {user?.role === "admin" ? "Admin" : "Agente"}
                    </span>
                  </div>
                </div>

                {user?.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => setUsersOpen(true)}
                    className="cursor-pointer py-1.5 text-xs text-slate-700 rounded-md"
                  >
                    <Users className="h-3.5 w-3.5 mr-2 text-slate-500" />
                    Gestión de Agentes
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={() => setAuditOpen(true)}
                  className="cursor-pointer py-1.5 text-xs text-slate-700 rounded-md"
                >
                  <History className="h-3.5 w-3.5 mr-2 text-slate-500" />
                  Registro de Auditoría
                </DropdownMenuItem>

                <DropdownMenuSeparator className="my-1 bg-slate-100" />

                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={() => {
                    logout();
                    onLogout?.();
                  }}
                  className="cursor-pointer py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50 rounded-md"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        
        {/* 1. Header Banner & View Switcher */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-1">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/70 shadow-2xs">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Sistema en línea
              </span>
              <span className="text-xs text-slate-300 font-medium hidden sm:inline">•</span>
              <span className="text-xs text-slate-500 font-medium hidden sm:inline">{formattedToday}</span>
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {greeting}, {user?.full_name ? user.full_name.split(" ")[0] : (user?.username || "Agente")}
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Monitoreo ejecutivo de reservas, facturación, pasarelas de pago y calendario de operaciones.
            </p>
          </div>

          {/* View switcher: Cards vs Table vs Calendar */}
          <div className="flex items-center rounded-xl border border-slate-200/90 bg-slate-100/80 p-1 shadow-2xs self-start lg:self-center shrink-0">
            <button
              onClick={() => changeViewMode("cards")}
              data-testid="view-cards-button"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === "cards"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <LayoutGrid className="h-4 w-4" /> Tarjetas
            </button>
            <button
              onClick={() => changeViewMode("table")}
              data-testid="view-table-button"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === "table"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <LayoutList className="h-4 w-4" /> Tabla
            </button>
            <button
              onClick={() => changeViewMode("calendar")}
              data-testid="view-calendar-button"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === "calendar"
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <CalendarDays className="h-4 w-4" /> Calendario
            </button>
          </div>
        </div>

        {/* 2. Executive Financial & Operational KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Total Expedientes */}
          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xs hover:shadow-xs transition-all">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Expedientes</span>
              <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Users className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3">
              <span className="font-heading text-3xl font-bold text-slate-900">{clients.length}</span>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-500">
                <span className="font-semibold text-emerald-600">{stats.verde || 0} confirmados</span>
                <span>•</span>
                <span>{stats.amarillo || 0} en proceso</span>
              </div>
            </div>
          </div>

          {/* Card 2: Total Facturado */}
          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xs hover:shadow-xs transition-all">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Facturado</span>
              <div className="h-9 w-9 rounded-xl bg-slate-100 text-slate-800 flex items-center justify-center">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3">
              <span className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                {formatMoney(financials.sales, currency)}
              </span>
              <p className="text-xs text-slate-400 mt-1.5">
                Volumen global de viajes contratados
              </p>
            </div>
          </div>

          {/* Card 3: Recaudación Efectiva */}
          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xs hover:shadow-xs transition-all">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Recaudado ({currency})</span>
              <div className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Wallet className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3">
              <span className="font-heading text-2xl sm:text-3xl font-bold text-emerald-700">
                {formatMoney(financials.paid, currency)}
              </span>
              <div className="mt-2 space-y-1">
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                    style={{ width: `${financials.percentPaid}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-500 font-medium">
                  {financials.percentPaid}% recaudado del total
                </p>
              </div>
            </div>
          </div>

          {/* Card 4: Saldo Pendiente */}
          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-2xs hover:shadow-xs transition-all">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Saldo Pendiente</span>
              <div className="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                <AlertCircle className="h-5 w-5" />
              </div>
            </div>
            <div className="mt-3">
              <span className="font-heading text-2xl sm:text-3xl font-bold text-amber-700">
                {formatMoney(financials.balance, currency)}
              </span>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-500">
                <span className="font-semibold text-amber-700">{financials.pendingClientsCount} clientes</span>
                <span>con saldo por cobrar</span>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Pipeline / Semáforo Interactive Filter Strip */}
        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-2xs space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-purple-600" />
              <h3 className="font-heading text-sm font-bold text-slate-800 tracking-tight">
                Flujo de Estados y Semáforo
              </h3>
              <span className="text-xs text-slate-400 hidden sm:inline">
                — Haz clic en cualquier etapa para filtrar la vista
              </span>
            </div>
            {statusFilter !== "todos" && (
              <button
                onClick={() => setStatusFilter("todos")}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 self-start sm:self-auto"
              >
                <RotateCcw className="h-3 w-3" /> Ver todos los estados
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {/* Ver Todos */}
            <button
              data-testid="stat-card-todos"
              onClick={() => {
                setStatusFilter("todos");
                setQuickFilter("todos");
                setPayFilter("todos");
                setTerminalFilter("todos");
              }}
              className={`relative text-left rounded-xl p-3.5 border transition-all ${
                statusFilter === "todos" && quickFilter === "todos" && payFilter === "todos" && terminalFilter === "todos"
                  ? "border-slate-900 bg-slate-900 text-white shadow-xs"
                  : "border-slate-200 bg-slate-50/50 hover:bg-slate-100/70 text-slate-700"
              }`}
            >
              <div className="flex items-center justify-between text-xs font-semibold">
                <span>Todos</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  statusFilter === "todos" && quickFilter === "todos" ? "bg-slate-800 text-slate-200" : "bg-slate-200/70 text-slate-600"
                }`}>
                  100%
                </span>
              </div>
              <p className="font-heading text-2xl font-bold mt-1.5">{clients.length}</p>
            </button>

            {/* Status Cards */}
            {STATUS_ORDER.map((key) => {
              const m = STATUS_META[key];
              const isSelected = statusFilter === key;
              const count = stats[key] || 0;
              const pct = clients.length > 0 ? Math.round((count / clients.length) * 100) : 0;

              return (
                <button
                  key={key}
                  data-testid={`stat-card-${key}`}
                  onClick={() => setStatusFilter(isSelected ? "todos" : key)}
                  className={`relative text-left rounded-xl p-3.5 border transition-all ${
                    isSelected
                      ? "border-slate-900 ring-2 ring-slate-900/20 bg-slate-50/80 shadow-xs"
                      : "border-slate-200 bg-white hover:bg-slate-50/60 text-slate-700"
                  }`}
                >
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-white" style={{ backgroundColor: m.hex }} />
                      <span className="font-semibold truncate text-[11px] sm:text-xs" title={labels?.[key] || m.name}>
                        {labels?.[key] || m.name}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono ml-1 shrink-0">{pct}%</span>
                  </div>
                  <p className="font-heading text-2xl font-bold text-slate-900 mt-1.5">{count}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* 4. Unified Search, Filters & Quick Chips Bar */}
        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 sm:p-5 shadow-2xs space-y-3.5">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search Input with Clear Button */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="client-search-input"
                placeholder="Buscar por cliente, expediente, hotel, asesor o terminal..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-9 h-10 bg-slate-50/50 border-slate-200 rounded-xl focus-visible:ring-1 focus-visible:ring-slate-800 text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-200/60"
                  title="Limpiar búsqueda"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Formas de Pago Filter */}
            <div className="w-full sm:w-56 shrink-0">
              <Select value={payFilter} onValueChange={setPayFilter}>
                <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200 rounded-xl text-xs font-medium" data-testid="payment-filter">
                  <div className="flex items-center gap-2 truncate">
                    <DollarSign className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <SelectValue placeholder="Forma de pago" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las formas de pago</SelectItem>
                  {settings.payment_methods.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Terminal Filter */}
            <div className="w-full sm:w-52 shrink-0">
              <Select value={terminalFilter} onValueChange={setTerminalFilter}>
                <SelectTrigger className="h-10 bg-slate-50/50 border-slate-200 rounded-xl text-xs font-medium" data-testid="terminal-filter">
                  <div className="flex items-center gap-2 truncate">
                    <CreditCard className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                    <SelectValue placeholder="Todas las terminales" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las terminales</SelectItem>
                  {terminals.map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name} {t.status === "bloqueada" ? " [BLOQUEADA]" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Filters Row + Reset + Counter */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-100">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-400 font-semibold flex items-center gap-1.5 text-[11px] uppercase tracking-wider">
                <Filter className="h-3.5 w-3.5" /> Filtros:
              </span>
              <button
                onClick={() => {
                  setQuickFilter("todos");
                  setPayFilter("todos");
                  setTerminalFilter("todos");
                  setStatusFilter("todos");
                }}
                className={`px-3 py-1 rounded-full border transition-all text-xs font-medium ${
                  quickFilter === "todos" && payFilter === "todos" && terminalFilter === "todos" && statusFilter === "todos"
                    ? "bg-slate-900 border-slate-900 text-white shadow-2xs"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                Todos ({clients.length})
              </button>
              <button
                onClick={() => setQuickFilter(quickFilter === "con_saldo" ? "todos" : "con_saldo")}
                className={`px-3 py-1 rounded-full border transition-all text-xs font-medium ${
                  quickFilter === "con_saldo"
                    ? "bg-slate-900 border-slate-900 text-white shadow-2xs"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                Con Saldo Pendiente
              </button>
              <button
                onClick={() => setQuickFilter(quickFilter === "sin_vuelos" ? "todos" : "sin_vuelos")}
                className={`px-3 py-1 rounded-full border transition-all text-xs font-medium ${
                  quickFilter === "sin_vuelos"
                    ? "bg-slate-900 border-slate-900 text-white shadow-2xs"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                Sin Vuelos Vendidos
              </button>
              <button
                onClick={() => setQuickFilter(quickFilter === "este_mes" ? "todos" : "este_mes")}
                className={`px-3 py-1 rounded-full border transition-all text-xs font-medium ${
                  quickFilter === "este_mes"
                    ? "bg-slate-900 border-slate-900 text-white shadow-2xs"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                Viajan Este Mes
              </button>

              {hasActiveFilters && (
                <button
                  onClick={resetAllFilters}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 text-xs font-semibold transition-colors border border-red-200/60"
                >
                  <X className="h-3 w-3" /> Limpiar filtros
                </button>
              )}
            </div>

            {/* Counter */}
            <div className="text-xs text-slate-500 font-medium shrink-0">
              Mostrando <span className="font-bold text-slate-900">{filtered.length}</span> de {clients.length} expedientes
            </div>
          </div>
        </div>

        {/* 5. View Mode Content (Cards / Table / Calendar) */}
        {viewMode === "calendar" ? (
          <CalendarView
            clients={filtered}
            onSelectClient={(c) => setDetailClient(c)}
            labels={labels}
          />
        ) : viewMode === "cards" ? (
          <ClientCardsView
            clients={filtered}
            labels={labels}
            currency={currency}
            convertAmount={convertAmount}
            onChangeStatus={changeStatus}
            onSelectClient={(c) => setDetailClient(c)}
            onEditClient={(c) => setEditClient(c)}
            onReservationClient={(c) => setResClient(c)}
            onDeleteClient={(c) => setDeleteTarget(c)}
            loading={loading}
          />
        ) : (
          /* Table View */
          <div className="rounded-2xl border border-slate-200/90 bg-white overflow-hidden shadow-2xs">
            <div className="overflow-x-auto">
              <Table data-testid="client-table">
                <TableHeader>
                  <TableRow className="bg-slate-50/90 border-b border-slate-200 hover:bg-slate-50/90">
                    <TableHead
                      onClick={() => toggleSort("code")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Expediente
                        {sortField === "code" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("name")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Titular / Cliente
                        {sortField === "name" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("travel_date")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Fecha Viaje
                        {sortField === "travel_date" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("hotel")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Hotel / Destino
                        {sortField === "hotel" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("terminal")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Terminal
                        {sortField === "terminal" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Asesor
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("paid")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Pagado / Total
                        {sortField === "paid" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("balance")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Saldo
                        {sortField === "balance" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      onClick={() => toggleSort("status")}
                      className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Estatus
                        {sortField === "status" ? (
                          sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 text-slate-300" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-16 text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="h-6 w-6 rounded-full border-2 border-slate-900 border-t-transparent animate-spin" />
                          <span className="text-xs font-medium text-slate-500">Cargando expedientes...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-16 text-slate-400">
                        <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
                          <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                            <Search className="h-6 w-6 stroke-[1.5]" />
                          </div>
                          <h4 className="font-heading text-base font-bold text-slate-800">No se encontraron expedientes</h4>
                          <p className="text-xs text-slate-400 mt-1 mb-4 text-center">
                            Ningún cliente coincide con los filtros aplicados. Prueba restablecer la búsqueda o los filtros.
                          </p>
                          {hasActiveFilters && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={resetAllFilters}
                              className="text-xs rounded-lg"
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restablecer filtros
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((c) => {
                      const paidUsd = (c.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                      const totalUsd = Number(c.total_amount) || 0;
                      const balanceUsd = Math.max(0, totalUsd - paidUsd);
                      const pctPaid = totalUsd > 0 ? Math.min(100, Math.round((paidUsd / totalUsd) * 100)) : (paidUsd > 0 ? 100 : 0);

                      const paidConv = convertAmount(paidUsd);
                      const totalConv = convertAmount(totalUsd);
                      const balanceConv = convertAmount(balanceUsd);

                      const isOpenDate = Boolean(c.reservation?.travel_date_open);
                      const tdateRaw = c.reservation?.travel_date;
                      let daysLeft = null;
                      if (!isOpenDate && tdateRaw) {
                        const target = new Date(tdateRaw + "T00:00:00");
                        const now = new Date();
                        now.setHours(0, 0, 0, 0);
                        const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
                        daysLeft = diff;
                      }

                      const initials = c.full_name
                        .split(" ")
                        .filter(Boolean)
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase();

                      return (
                        <TableRow
                          key={c.id}
                          data-testid="client-table-row"
                          className="cursor-pointer hover:bg-slate-50/80 transition-colors group"
                          onClick={() => setDetailClient(c)}
                        >
                          {/* Expediente Code */}
                          <TableCell className="py-3">
                            <span className="font-mono text-[11px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/60">
                              {c.client_code}
                            </span>
                          </TableCell>

                          {/* Cliente Info */}
                          <TableCell className="py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-slate-900 to-slate-700 text-white font-bold text-xs flex items-center justify-center shrink-0 ring-2 ring-white shadow-2xs">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <span className="font-semibold text-slate-900 text-sm block truncate group-hover:text-blue-600 transition-colors">
                                  {c.full_name}
                                </span>
                                {c.phone ? (
                                  <span className="text-[11px] text-slate-400 font-mono flex items-center gap-1 truncate">
                                    <Phone className="h-2.5 w-2.5" /> {c.phone}
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-slate-300">Sin teléfono</span>
                                )}
                              </div>
                            </div>
                          </TableCell>

                          {/* Fecha Viaje */}
                          <TableCell className="py-3">
                            {isOpenDate ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-200/60">
                                <CalendarDays className="h-3 w-3" /> Fecha abierta
                              </span>
                            ) : tdateRaw ? (
                              <div className="space-y-0.5">
                                <span className="text-xs font-semibold text-slate-700 block">
                                  {formatDate(tdateRaw)}
                                </span>
                                {daysLeft !== null && (
                                  <span
                                    className={`inline-block text-[10px] font-bold px-1.5 py-0.2 rounded-full ${
                                      daysLeft <= 0
                                        ? "bg-slate-100 text-slate-500"
                                        : daysLeft <= 3
                                        ? "bg-red-50 text-red-600 border border-red-200/60"
                                        : daysLeft <= 15
                                        ? "bg-amber-50 text-amber-700 border border-amber-200/60"
                                        : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {daysLeft === 0
                                      ? "Viaja hoy"
                                      : daysLeft === 1
                                      ? "Mañana"
                                      : daysLeft > 1
                                      ? `En ${daysLeft} días`
                                      : "Viaje realizado"}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Hotel / Destino */}
                          <TableCell className="py-3 text-sm text-slate-600 max-w-[180px]">
                            {c.hotel ? (
                              <span className="flex items-center gap-1.5 truncate" title={c.hotel}>
                                <HotelIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                <span className="truncate font-medium text-xs text-slate-800">{c.hotel}</span>
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Terminal de Cobro */}
                          <TableCell className="py-3">
                            {c.terminal ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200/60 max-w-[150px] truncate shadow-2xs"
                                title={`Terminal asignada: ${c.terminal}`}
                              >
                                <CreditCard className="h-3 w-3 text-blue-500 shrink-0" />
                                <span className="truncate">{c.terminal}</span>
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Asesor */}
                          <TableCell className="py-3 text-xs text-slate-500">
                            {c.assigned_to ? (
                              <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono text-[11px] font-medium border border-slate-200/50">
                                @{c.assigned_to}
                              </span>
                            ) : (
                              <span className="text-slate-300 text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Pagado / Total con Barra de Progreso */}
                          <TableCell className="py-3" data-testid="client-paid-cell">
                            <div className="leading-tight space-y-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold text-emerald-700">
                                  {formatMoney(paidConv, currency)}
                                </span>
                                {totalUsd > 0 && (
                                  <span className="text-[10px] text-slate-400">
                                    / {formatMoney(totalConv, currency)}
                                  </span>
                                )}
                              </div>
                              {totalUsd > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full transition-all ${
                                        pctPaid >= 100 ? "bg-emerald-600" : "bg-blue-600"
                                      }`}
                                      style={{ width: `${pctPaid}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] font-mono font-medium text-slate-400">
                                    {pctPaid}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </TableCell>

                          {/* Saldo Pendiente */}
                          <TableCell className="py-3">
                            {balanceUsd <= 0.01 ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                                <CheckCircle2 className="h-3 w-3" /> Liquidado
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60">
                                {formatMoney(balanceConv, currency)}
                              </span>
                            )}
                          </TableCell>

                          {/* Estatus Semáforo */}
                          <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                            <Semaphore
                              status={c.status}
                              labels={labels}
                              size="sm"
                              onChange={(s) => changeStatus(c.id, s)}
                            />
                          </TableCell>

                          {/* Acciones */}
                          <TableCell className="py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-400 hover:text-slate-700 rounded-lg"
                                  data-testid="client-actions-trigger"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48 shadow-lg border-slate-200 p-1.5">
                                <DropdownMenuItem onClick={() => setDetailClient(c)} className="cursor-pointer py-1.5 text-xs text-slate-700">
                                  <Eye className="h-4 w-4 mr-2 text-slate-500" /> Ver Ficha Completa
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setResClient(c)} className="cursor-pointer py-1.5 text-xs text-slate-700">
                                  <Plane className="h-4 w-4 mr-2 text-blue-600" /> Datos de Reserva
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setEditClient(c)} className="cursor-pointer py-1.5 text-xs text-slate-700">
                                  <Pencil className="h-4 w-4 mr-2 text-amber-600" /> Editar Cliente
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="my-1 bg-slate-100" />
                                <DropdownMenuItem
                                  className="cursor-pointer py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 focus:text-red-700 focus:bg-red-50"
                                  onClick={() => setDeleteTarget(c)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" /> Eliminar Expediente
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <ClientFormDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        methods={settings.payment_methods}
        labels={labels}
        terminals={terminals}
        onSaved={(created) => {
          setClients((prev) => [created, ...prev]);
          refreshReminders();
        }}
      />
      <ClientFormDialog
        open={!!editClient}
        onOpenChange={(v) => !v && setEditClient(null)}
        client={editClient}
        methods={settings.payment_methods}
        labels={labels}
        terminals={terminals}
        onSaved={(updated) => {
          upsert(updated);
          setEditClient(null);
          refreshReminders();
        }}
      />
      <PaymentMethodsDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        methods={settings.payment_methods}
        onSaved={(methods) => setSettings((s) => ({ ...s, payment_methods: methods }))}
      />
      <StatusLabelsDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        labels={settings.status_labels}
        onSaved={(l) => setSettings((s) => ({ ...s, status_labels: l }))}
      />
      <ClientDetailSheet
        open={!!detail}
        onOpenChange={(v) => !v && setDetailClient(null)}
        client={detail}
        methods={settings.payment_methods}
        labels={labels}
        currency={currency}
        terminals={terminals}
        onUpdate={(updated) => {
          upsert(updated);
          setDetailClient(updated);
          refreshReminders();
        }}
        onOpenReservation={(c) => {
          setResClient(c);
        }}
        onEdit={(c) => {
          setDetailClient(null);
          setEditClient(c);
        }}
      />
      <TerminalsDialog
        open={terminalsOpen}
        onOpenChange={setTerminalsOpen}
        onTerminalsUpdated={loadTerminals}
      />
      <ReservationDialog
        open={!!resClient}
        onOpenChange={(v) => !v && setResClient(null)}
        client={resClient}
        onSaved={(updated) => {
          upsert(updated);
          setResClient(null);
          refreshReminders();
        }}
      />
      <UsersDialog
        open={usersOpen}
        onOpenChange={setUsersOpen}
        currentUser={user}
      />
      <AuditDialog
        open={auditOpen}
        onOpenChange={setAuditOpen}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-client-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.full_name}</strong> y todos sus datos. Esta acción
              no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
              onClick={confirmDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
